/**
 * Consent state, kept deliberately separate from the analytics layer so that the
 * question "may we track this person?" has exactly one answer in the app and the
 * tracking code never has to guess.
 *
 * Three categories, matching how the regulators think about them:
 *  - essential      always on; session, auth, cart. Never gated.
 *  - analytics      product measurement (GA4).
 *  - advertising    ad platform pixels and remarketing (Meta, TikTok, LinkedIn, Google Ads).
 *
 * Nothing beyond `essential` runs until the visitor has actively chosen.
 */

export type ConsentCategory = 'essential' | 'analytics' | 'advertising';

export interface ConsentState {
  analytics: boolean;
  advertising: boolean;
  /** ISO timestamp of the decision — needed to prove when consent was given. */
  decidedAt: string | null;
}

const STORAGE_KEY = 'mdx-consent';

/** Consent version. Bump when the categories change so visitors are re-asked. */
const VERSION = 1;

const DENIED: ConsentState = { analytics: false, advertising: false, decidedAt: null };

/** Granted, but with `decidedAt: null` — the banner still shows and asks. */
const GRANTED_PENDING: ConsentState = { analytics: true, advertising: true, decidedAt: null };

/**
 * Rough "is this visitor somewhere that requires prior opt-in?" check.
 *
 * Read from the browser's own timezone: no network call, no third-party lookup,
 * available synchronously on first paint — which matters, because the decision
 * has to be made before the first event fires.
 *
 * Deliberately errs towards opt-in. An unreadable timezone is treated as a
 * consent-required region.
 */
const CONSENT_REQUIRED_PREFIXES = ['Europe/', 'Atlantic/Azores', 'Atlantic/Canary', 'Atlantic/Madeira'];

const requiresPriorConsent = (): boolean => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return true;
    return CONSENT_REQUIRED_PREFIXES.some((prefix) => zone.startsWith(prefix));
  } catch {
    return true;
  }
};

/**
 * What applies before the visitor has answered.
 *
 * Under GDPR/UK GDPR nothing non-essential may run before an explicit yes, so
 * European visitors start fully denied. Outside that, the NDPR and equivalents
 * work on notice and a real opt-out, which is what the banner provides — so
 * measurement starts on and the visitor can switch it off.
 *
 * This was denied-by-default everywhere. Since most visitors never touch a
 * cookie banner, that meant the Meta and TikTok pixels almost never fired: ad
 * platforms had nothing to optimise against and campaign spend was flying blind
 * against its own conversion data.
 */
const defaultState = (): ConsentState => (requiresPriorConsent() ? DENIED : GRANTED_PENDING);

const listeners = new Set<(state: ConsentState) => void>();

let current: ConsentState = DENIED;
let hydrated = false;

const read = (): ConsentState => {
  if (typeof window === 'undefined') return DENIED;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as ConsentState & { version?: number };
    if (parsed.version !== VERSION) return defaultState();
    return {
      analytics: Boolean(parsed.analytics),
      advertising: Boolean(parsed.advertising),
      decidedAt: parsed.decidedAt ?? null,
    };
  } catch {
    return defaultState();
  }
};

export const getConsent = (): ConsentState => {
  if (!hydrated) {
    current = read();
    hydrated = true;
  }
  return current;
};

/** True once the visitor has answered, either way. Drives the banner's visibility. */
export const hasDecided = (): boolean => getConsent().decidedAt !== null;

export const setConsent = (next: Pick<ConsentState, 'analytics' | 'advertising'>): void => {
  current = { ...next, decidedAt: new Date().toISOString() };
  hydrated = true;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, version: VERSION }));
  } catch {
    // Private browsing with storage disabled: consent then lasts the session only,
    // which is the conservative outcome — the visitor is simply asked again.
  }
  listeners.forEach((listener) => listener(current));
};

export const acceptAll = () => setConsent({ analytics: true, advertising: true });
export const rejectAll = () => setConsent({ analytics: false, advertising: false });

export const onConsentChange = (listener: (state: ConsentState) => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const isAllowed = (category: ConsentCategory): boolean => {
  if (category === 'essential') return true;
  return getConsent()[category];
};
